import type { MapMarker, MapMarkerCircleStyle } from '@gpx-spa/geo-domain';
import { formatCurrentPositionPopup } from '@/utils/current-position-popup';

/** Matches v2 current-positions proxy rows (seconds timestamps, lng/lat pair). */
export type V2CurrentPositionLike = {
  device_id: number;
  location?: [number, number];
  address?: string | null;
  battery?: number | null;
  fix?: string;
  /** Unix seconds — used by convoy proximity leader selection. */
  timestamp?: number;
  /** Meters; used with {@link accuracyRadiusMetersForRow} when the app should show an uncertainty ring. */
  accuracy?: number | null;
};

function rowPopupHtml(row: V2CurrentPositionLike): string {
  return formatCurrentPositionPopup({
    address: row.address,
    location: row.location,
    timestampSec: row.timestamp,
    battery: row.battery,
    fix: row.fix,
    accuracyMeters: row.accuracy,
  });
}

/**
 * Accuracy ring: only for **wifi** or **cell** fix when reported accuracy is **above 500 m** (coarse fixes).
 * GPS / other fix types omit the ring here; providers only draw when `accuracyRadiusMeters` is set.
 */
export function accuracyRadiusMetersForRow(
  row: V2CurrentPositionLike,
): number | undefined {
  const fixRaw = row.fix;
  const fix = typeof fixRaw === 'string' ? fixRaw.trim().toLowerCase() : '';
  const acc = row.accuracy;
  if (acc == null || !Number.isFinite(acc) || acc <= 0) return undefined;
  if ((fix === 'cell' || fix === 'wifi') && acc > 500) return acc;
  return undefined;
}

/** Blue pulsing dot for live device position on shipment operational map (Leaflet). */
export const V2_CURRENT_POSITION_MARKER_STYLE: MapMarkerCircleStyle = {
  kind: 'circle',
  fillColor: '#2563eb',
  borderColor: '#ffffff',
  pulse: true,
};

/**
 * One pin per row. Ids are stable for upserts: `device-{device_id}`.
 */
export function v2CurrentPositionRowsToMapMarkers(
  rows: V2CurrentPositionLike[],
): MapMarker[] {
  const out: MapMarker[] = [];
  for (const row of rows) {
    const loc = row.location;
    if (!loc || loc.length < 2) continue;
    const [lng, lat] = loc;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const accuracyRadiusMeters = accuracyRadiusMetersForRow(row);
    out.push({
      id: `device-${row.device_id}`,
      position: { lat, lng },
      style: V2_CURRENT_POSITION_MARKER_STYLE,
      ...(accuracyRadiusMeters != null ? { accuracyRadiusMeters } : {}),
      popup: rowPopupHtml(row),
      popupHtml: true,
    });
  }
  return out;
}
